import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that displays a "Shop the Look" panel.
 *
 * Opens a right-side dialog listing products; each product row has an optional
 * variant select and an "Add to cart" button. Adding to cart updates the header
 * cart-count bubble without closing the modal.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button that opens the dialog.
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement[]} addButtons - The per-product "Add to cart" buttons.
 *
 * @extends {DialogComponent<Refs>}
 */
class ShopTheLook extends DialogComponent {
  requiredRefs = ['dialog'];

  /**
   * Handles the "Add to cart" click for a product row.
   *
   * Reads the selected variant id from the nearest `[data-variant-select]` inside
   * the same `[data-product-row]`, falling back to `data-variant-id` on the button
   * itself. POSTs to the cart, updates the cart-count bubble, then reverts the
   * button label after ~2 s. Does NOT close the dialog.
   *
   * @param {MouseEvent & { target: HTMLButtonElement }} event - The click event,
   *   with `event.target` proxied to the element carrying `on:click` by component.js.
   */
  addToCart = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const row = button.closest('[data-product-row]');
    const select = /** @type {HTMLSelectElement | null} */ (
      row?.querySelector('select[data-variant-select]')
    );
    const id = select ? select.value : button.dataset.variantId;

    if (!id) return;

    button.setAttribute('disabled', '');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });

      const result = await response.json();

      // A truthy `status` field indicates an error from Shopify (e.g. 422 sold out).
      if (result.status) {
        return;
      }

      // Swap the label to the "added" text.
      const addedLabel = button.dataset.addedLabel || 'Added to cart';
      const originalLabel = button.dataset.originalLabel || button.textContent || '';
      button.dataset.originalLabel = originalLabel;
      button.textContent = addedLabel;

      // Fetch the full cart so we can SET (not increment) the cart-count bubble.
      // cart-icon.js increments only when source === 'product-form-component';
      // any other source causes it to set the bubble to itemCount directly.
      const cart = await (await fetch('/cart.js')).json();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'shop-the-look', {
          source: 'shop-the-look',
          itemCount: cart.item_count,
        })
      );

      // Revert the label after 2 s; the dialog stays open.
      setTimeout(() => {
        button.textContent = button.dataset.originalLabel;
      }, 2000);
    } finally {
      button.removeAttribute('disabled');
    }
  };

  /**
   * Swaps the product row image to the selected variant's image.
   *
   * Reads the `data-image-*` attributes off the selected `<option>` and applies
   * them to the `img.shop-the-look__item-image` inside the same `[data-product-row]`.
   * Does nothing if the row has no image or the option has no image data.
   *
   * @param {Event & { target: HTMLSelectElement }} event - The change event,
   *   with `event.target` proxied to the element carrying `on:change` by component.js.
   */
  onVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const option = select.selectedOptions[0];
    const row = select.closest('[data-product-row]');
    const image = /** @type {HTMLImageElement | null} */ (
      row?.querySelector('img.shop-the-look__item-image')
    );

    if (!image || !option?.dataset.imageSrc) return;

    image.src = option.dataset.imageSrc;
    image.srcset = option.dataset.imageSrcset ?? '';
    // Assign unconditionally: guarding on a truthy value left the previously selected
    // variant's alt describing the new image whenever the incoming one was empty.
    image.alt = option.dataset.imageAlt ?? '';
  };
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
